import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { FORMA_LINK_BTN } from '../../utils/listPageUi';
import localStorageService from '../../utils/localStorageService';
import {
  PlusIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ClockIcon,
  TrashIcon,
  PencilIcon,
  AcademicCapIcon,
  TagIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

const BatchForm = ({ courseId, courseName, onBatchCreated, onCancel, isEditing = false, batchData = null, classrooms = [] }) => {
  const [trainers, setTrainers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    batchName: batchData?.batchName || '',
    days: batchData?.days || [],
    startTime: batchData?.startTime || '',
    endTime: batchData?.endTime || '',
    startDate: batchData?.startDate || '',
    endDate: batchData?.endDate || '',
    trainerId: batchData?.trainerId || '',
    room: batchData?.room || '',
    maxCapacity: batchData?.maxCapacity || 15,
    courseId: courseId || batchData?.courseId || '',
    courseName: courseName || batchData?.courseName || ''
  });
  const [conflicts, setConflicts] = useState([]);

  // Load trainers and courses
  useEffect(() => {
    const storedTrainers = localStorageService.getAllItems('trainers') || [];
    const storedCourses = localStorageService.getAllItems('courses') || [];
    setTrainers(storedTrainers);
    setCourses(storedCourses);
  }, []);

  const weekDays = [
    { id: 'Monday', name: 'Lundi' },
    { id: 'Tuesday', name: 'Mardi' },
    { id: 'Wednesday', name: 'Mercredi' },
    { id: 'Thursday', name: 'Jeudi' },
    { id: 'Friday', name: 'Vendredi' },
    { id: 'Saturday', name: 'Samedi' },
    { id: 'Sunday', name: 'Dimanche' }
  ];

  const handleDayToggle = (dayId) => {
    setFormData(prev => ({
      ...prev,
      days: prev.days.includes(dayId)
        ? prev.days.filter(d => d !== dayId)
        : [...prev.days, dayId]
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'courseId') {
      const selectedCourse = Array.isArray(courses) ? courses.find(c => c.id === value) : null;
      setFormData(prev => ({
        ...prev,
        [name]: value,
        courseName: selectedCourse?.title || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Check for conflicts when trainer, days, times, or dates change
  useEffect(() => {
    if (formData.trainerId && formData.days.length > 0 && formData.startTime && formData.endTime && formData.startDate && formData.endDate) {
      const conflictingBatches = localStorageService.checkTrainerConflict(
        formData.trainerId,
        formData.days,
        formData.startTime,
        formData.endTime,
        formData.startDate,
        formData.endDate,
        isEditing ? batchData?.id : null // Exclude current batch when editing
      );
      setConflicts(conflictingBatches);
    } else {
      setConflicts([]);
    }
  }, [formData.trainerId, formData.days, formData.startTime, formData.endTime, formData.startDate, formData.endDate, isEditing, batchData?.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (conflicts.length > 0) {
      alert('Il y a des conflits d\'horaires avec ce formateur. Veuillez choisir un autre créneau ou formateur.');
      return;
    }

    try {
      const selectedTrainer = Array.isArray(trainers) ? trainers.find(t => t.id === formData.trainerId) : null;
      
      const batchDataToSave = {
        ...formData,
        trainerName: selectedTrainer?.name || 'Unknown'
      };

      if (isEditing && batchData) {
        // Update existing batch
        const updatedBatch = {
          ...batchData,
          ...batchDataToSave,
          id: batchData.id, // Keep the original ID
          updatedAt: new Date().toISOString() // Add timestamp for tracking
        };
        
        console.log('🔄 Updating batch with ID:', batchData.id);
        console.log('📝 Original batch data:', batchData);
        console.log('📝 New batch data to save:', batchDataToSave);
        console.log('📝 Final updated batch:', updatedBatch);
        
        const result = localStorageService.updateItem('batches', updatedBatch);
        console.log('✅ Update result:', result);
        
        if (result) {
          console.log('✅ Batch successfully updated in localStorage');
          alert('✅ Groupe modifié avec succès!');
          onBatchCreated(updatedBatch);
        } else {
          console.error('❌ Failed to update batch in localStorage');
          alert('❌ Erreur: Impossible de sauvegarder les modifications');
        }
      } else {
        // Create new batch
        const newBatch = localStorageService.createBatch(batchDataToSave);
        console.log('✅ Batch created:', newBatch);
        alert('✅ Nouveau groupe créé avec succès!');
        onBatchCreated(newBatch);
        
        // Reset form only for creation
        setFormData({
          batchName: '',
          days: [],
          startTime: '',
          endTime: '',
          startDate: '',
          endDate: '',
          trainerId: '',
          room: '',
          maxCapacity: 15,
          courseId: '',
          courseName: ''
        });
      }
    } catch (error) {
      console.error('Error saving batch:', error);
      alert('Erreur lors de l\'enregistrement du groupe');
    }
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">
        {isEditing ? 'Modifier le groupe' : 'Créer un nouveau groupe'}
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Course Selection */}
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Cours
            </label>
            <select
              name="courseId"
              value={formData.courseId}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Sélectionner un cours</option>
              {Array.isArray(courses) && courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>

          {/* Group Name */}
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Nom du groupe
            </label>
            <input
              type="text"
              name="batchName"
              value={formData.batchName}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Groupe A, Matin, Soir..."
              required
            />
          </div>

          {/* Days Selection */}
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Jours de la semaine
            </label>
            <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
              {weekDays.map(day => (
                <button
                  key={day.id}
                  type="button"
                  onClick={() => handleDayToggle(day.id)}
                  className={`p-2 rounded-lg text-sm font-medium transition-all ${
                    formData.days.includes(day.id)
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100/80 dark:bg-slate-800/40 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/80'
                  }`}
                >
                  {day.name}
                </button>
              ))}
            </div>
          </div>

          {/* Time Range */}
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Heure de début
            </label>
            <input
              type="time"
              name="startTime"
              value={formData.startTime}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Heure de fin
            </label>
            <input
              type="time"
              name="endTime"
              value={formData.endTime}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Date de début
            </label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Date de fin
            </label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Trainer */}
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Formateur
            </label>
            <select
              name="trainerId"
              value={formData.trainerId}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Sélectionner un formateur</option>
              {Array.isArray(trainers) && trainers.map(trainer => (
                <option key={trainer.id} value={trainer.id}>
                  {trainer.name}
                </option>
              ))}
            </select>
          </div>

          {/* Capacity */}
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Capacité maximale
            </label>
            <input
              type="number"
              name="maxCapacity"
              value={formData.maxCapacity}
              onChange={handleChange}
              min="1"
              max="50"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Room */}
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Salle (optionnel)
            </label>
            <select
              name="room"
              value={formData.room}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Sélectionner une salle</option>
              {Array.isArray(classrooms) && classrooms.map(classroom => (
                <option key={classroom.id} value={classroom.name}>
                  {classroom.name} ({classroom.code}) - Capacité: {classroom.capacity} {!classroom.isAvailable ? ' - OCCUPÉE' : ''}
                </option>
              ))}
            </select>
            
            {/* Show selected room details */}
            {formData.room && (() => {
              const selectedRoom = classrooms.find(room => room.name === formData.room);
              return selectedRoom ? (
                <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-blue-300 font-medium text-sm">{selectedRoom.name} ({selectedRoom.code})</h4>
                      <p className="text-blue-200 text-xs">
                        Capacité: {selectedRoom.capacity} • Étage: {selectedRoom.floor || 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        selectedRoom.isAvailable 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {selectedRoom.isAvailable ? 'Disponible' : 'Occupée'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Equipment icons */}
                  <div className="flex items-center space-x-3 mt-2">
                    {selectedRoom.hasProjector && (
                      <span className="text-blue-300 text-xs">📽️ Projecteur</span>
                    )}
                    {selectedRoom.hasAirConditioning && (
                      <span className="text-blue-300 text-xs">❄️ Clim</span>
                    )}
                    {selectedRoom.hasComputers && (
                      <span className="text-blue-300 text-xs">💻 PC</span>
                    )}
                    {selectedRoom.hasWhiteboard && (
                      <span className="text-blue-300 text-xs">📋 Tableau</span>
                    )}
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        </div>

        {/* Enhanced Conflict warnings */}
        {conflicts.length > 0 && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mr-2" />
              <div>
                <span className="text-red-400 font-medium text-lg">⚠️ Conflits d'horaires détectés!</span>
                <p className="text-red-300 text-xs mt-1">Le formateur sélectionné a déjà des cours aux mêmes créneaux</p>
              </div>
            </div>
            
            <div className="bg-red-500/10 rounded-lg p-3 mb-3">
              <h4 className="text-red-300 font-medium text-sm mb-2">📅 Cours en conflit:</h4>
              <div className="space-y-2">
                {conflicts.map(conflict => (
                  <div key={conflict.id} className="bg-red-500/20 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-red-200 font-medium">{conflict.courseName}</div>
                        <div className="text-red-300 text-xs">{conflict.batchName}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-red-200 text-sm">{conflict.days.join(', ')}</div>
                        <div className="text-red-300 text-xs">{conflict.startTime} - {conflict.endTime}</div>
                      </div>
                    </div>
                    {conflict.room && (
                      <div className="text-red-300 text-xs mt-1">🏫 Salle: {conflict.room}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
              <h4 className="text-yellow-300 font-medium text-sm mb-2">💡 Solutions suggérées:</h4>
              <ul className="text-yellow-200 text-xs space-y-1">
                <li>• Choisir un autre formateur disponible</li>
                <li>• Modifier les jours de la semaine</li>
                <li>• Changer les heures de début/fin</li>
                <li>• Reporter ce groupe à une autre période</li>
              </ul>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={conflicts.length > 0}
            className={clsx(
              FORMA_LINK_BTN,
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {isEditing ? 'Modifier le groupe' : 'Créer le groupe'}
          </button>
        </div>
      </form>
    </div>
  );
};

const GroupCard = ({ batch, onEdit, onDelete, onViewStudents }) => {
  const batchWithEnrollment = localStorageService.getBatchWithEnrollment(batch.id);
  
  // Translation mapping for days
  const dayTranslations = {
    'Monday': 'Lundi',
    'Tuesday': 'Mardi', 
    'Wednesday': 'Mercredi',
    'Thursday': 'Jeudi',
    'Friday': 'Vendredi',
    'Saturday': 'Samedi',
    'Sunday': 'Dimanche'
  };
  
  // Get fresh batch data to ensure real-time updates
  const freshBatch = localStorageService.getItemById('batches', batch.id) || batch;
  const freshBatchWithEnrollment = localStorageService.getBatchWithEnrollment(freshBatch.id);
  
  // Debug log to track real-time data
  console.log('📊 GroupCard Data:', {
    batchId: freshBatch.id,
    batchName: freshBatch.batchName,
    courseName: freshBatch.courseName,
    trainerName: freshBatch.trainerName,
    room: freshBatch.room,
    days: freshBatch.days,
    startTime: freshBatch.startTime,
    endTime: freshBatch.endTime,
    startDate: freshBatch.startDate,
    endDate: freshBatch.endDate,
    maxCapacity: freshBatch.maxCapacity,
    currentEnrollment: freshBatchWithEnrollment?.currentEnrollment,
    isFull: freshBatchWithEnrollment?.isFull
  });
  
  // Translate days to French
  const translatedDays = Array.isArray(freshBatch.days) 
    ? freshBatch.days.map(day => dayTranslations[day] || day).join(', ')
    : 'Non définis';
  
  return (
    <div className="bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl p-4 border border-slate-200 dark:border-slate-600">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-slate-900 dark:text-slate-50 font-medium">{freshBatch.batchName || 'Nom non défini'}</h4>
          <p className="text-slate-600 dark:text-slate-400 text-sm">{freshBatch.courseName || 'Cours non défini'}</p>
          <p className="text-slate-600 dark:text-slate-400 text-xs">
            {translatedDays} • {freshBatch.startTime || 'N/A'} - {freshBatch.endTime || 'N/A'}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 rounded-full text-xs ${
            freshBatchWithEnrollment?.isFull 
              ? 'bg-red-500/20 text-red-400' 
              : 'bg-green-500/20 text-green-400'
          }`}>
            {freshBatchWithEnrollment?.currentEnrollment || 0}/{freshBatch.maxCapacity || 'N/A'}
          </span>
          <button
            onClick={() => onViewStudents(freshBatch)}
            className="p-1 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-colors"
            title="Voir les étudiants"
          >
            <UserGroupIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onEdit(freshBatch)}
            className="p-1 bg-yellow-500/20 text-yellow-400 rounded hover:bg-yellow-500/30 transition-colors"
            title="Modifier le groupe"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(freshBatch.id)}
            className="p-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
            title="Supprimer"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <span className="text-slate-600 dark:text-slate-400">Formateur:</span>
          <p className="text-slate-900 dark:text-slate-50">{freshBatch.trainerName || 'Non assigné'}</p>
        </div>
        <div>
          <span className="text-slate-600 dark:text-slate-400">Salle:</span>
          <p className="text-slate-900 dark:text-slate-50">{freshBatch.room || 'Non définie'}</p>
        </div>
        <div>
          <span className="text-slate-600 dark:text-slate-400">Début:</span>
          <p className="text-slate-900 dark:text-slate-50">
            {freshBatch.startDate 
              ? new Date(freshBatch.startDate).toLocaleDateString('fr-FR') 
              : 'Non définie'}
          </p>
        </div>
        <div>
          <span className="text-slate-600 dark:text-slate-400">Fin:</span>
          <p className="text-slate-900 dark:text-slate-50">
            {freshBatch.endDate 
              ? new Date(freshBatch.endDate).toLocaleDateString('fr-FR') 
              : 'Non définie'}
          </p>
        </div>
      </div>

      {/* Enrollment progress bar */}
      <div className="mt-3">
        <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400 mb-1">
          <span>Inscriptions</span>
          <span>
            {freshBatch.maxCapacity > 0 
              ? (((freshBatchWithEnrollment?.currentEnrollment || 0) / freshBatch.maxCapacity) * 100).toFixed(0)
              : 0}%
          </span>
        </div>
        <div className="w-full h-2 bg-slate-50 dark:bg-slate-800/80 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full bg-gradient-to-r from-forma-500 to-forma-700 transition-all duration-300" 
            style={{ 
              width: `${freshBatch.maxCapacity > 0 
                ? (((freshBatchWithEnrollment?.currentEnrollment || 0) / freshBatch.maxCapacity) * 100)
                : 0}%` 
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

const GroupsPage = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedBatchStudents, setSelectedBatchStudents] = useState(null);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [filterCourse, setFilterCourse] = useState('');

  // Fetch batches with real-time data
  const { data: batchesData, isLoading, refetch } = useQuery(
    'batches',
    () => {
      // Get fresh data every time
      const batches = localStorageService.getAllBatchesWithEnrollment();
      console.log('🔄 Fetching fresh batches data:', batches.length, 'batches found');
      return { batches };
    },
    {
      staleTime: 0,
      cacheTime: 0, // Don't cache to ensure fresh data
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchInterval: 5000 // Refetch every 5 seconds for real-time updates
    }
  );

  // Fetch courses for filter
  const { data: coursesData } = useQuery(
    'courses',
    () => {
      const courses = localStorageService.getAllItems('courses');
      console.log('📚 Fetching fresh courses data:', courses.length, 'courses found');
      return courses;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  );

  // Fetch classrooms for room selection
  const { data: classroomsData } = useQuery(
    'classrooms',
    () => {
      const classrooms = localStorageService.getAllItems('classrooms');
      console.log('🏫 Fetching fresh classrooms data:', classrooms.length, 'classrooms found');
      return classrooms;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  );

  const batches = batchesData?.batches || [];
  const courses = Array.isArray(coursesData) ? coursesData : [];
  const classrooms = Array.isArray(classroomsData) ? classroomsData : [];

  // Enhance batches with fresh course names
  const batchesWithCorrectCourseNames = batches.map(batch => {
    const correspondingCourse = courses.find(course => course.id === batch.courseId);
    return {
      ...batch,
      courseName: correspondingCourse?.title || batch.courseName || 'Cours non trouvé'
    };
  });

  // Filter batches by course using enhanced data
  const filteredBatches = filterCourse 
    ? batchesWithCorrectCourseNames.filter(batch => batch.courseId === filterCourse)
    : batchesWithCorrectCourseNames;

  // Group batches by course using enhanced data
  const batchesByCourse = filteredBatches.reduce((acc, batch) => {
    const courseId = batch.courseId;
    if (!acc[courseId]) {
      acc[courseId] = [];
    }
    acc[courseId].push(batch);
    return acc;
  }, {});

  const formaPrimaryBtn = FORMA_LINK_BTN;

  const courseFilterTabs = useMemo(
    () => [{ id: '', name: 'Tous les cours' }, ...courses.map((c) => ({ id: c.id, name: c.title }))],
    [courses]
  );

  const courseTabCounts = useMemo(() => {
    const counts = { '': batchesWithCorrectCourseNames.length };
    courses.forEach((c) => {
      counts[c.id] = batchesWithCorrectCourseNames.filter((b) => b.courseId === c.id).length;
    });
    return counts;
  }, [batchesWithCorrectCourseNames, courses]);

  const groupsStats = [
    {
      title: 'Groupes',
      value: batchesWithCorrectCourseNames.length.toString(),
      hint: 'Toutes formations',
      icon: TagIcon,
      iconWrap:
        'bg-forma-100 text-forma-700 shadow-inner dark:bg-forma-950/50 dark:text-forma-400',
    },
    {
      title: 'Cours couverts',
      value: new Set(batchesWithCorrectCourseNames.map((b) => b.courseId)).size.toString(),
      hint: 'Avec au moins un groupe',
      icon: AcademicCapIcon,
      iconWrap:
        'bg-emerald-100 text-emerald-800 shadow-inner dark:bg-emerald-950/45 dark:text-emerald-400',
    },
    {
      title: 'Places cumulées',
      value: batchesWithCorrectCourseNames.reduce((s, b) => s + (Number(b.maxCapacity) || 0), 0).toString(),
      hint: 'Capacité max des groupes',
      icon: UserGroupIcon,
      iconWrap: 'bg-rose-100 text-rose-800 shadow-inner dark:bg-rose-950/40 dark:text-rose-400',
    },
  ];

  const handleBatchCreated = (newBatch) => {
    console.log('🎯 handleBatchCreated called with:', newBatch);
    setShowForm(false);
    setShowEditForm(false);
    setEditingBatch(null);
    
    // Trigger multiple data refresh methods to ensure update
    console.log('🔄 Triggering comprehensive data refresh...');
    
    // Method 1: Standard refetch
    refetch();
    
    // Method 2: Force component re-render with a small delay
    setTimeout(() => {
      refetch();
      console.log('✅ Secondary refetch completed');
    }, 100);
    
    // Method 3: Manual state update for immediate UI feedback
    queryClient.invalidateQueries('batches');
    console.log('✅ Query cache invalidated');
  };

  const handleEditBatch = (batch) => {
    setEditingBatch(batch);
    setShowEditForm(true);
    setShowForm(false);
  };

  const handleDeleteBatch = (batchId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce groupe ?')) {
      localStorageService.deleteItem('batches', batchId);
      refetch();
    }
  };

  const handleViewStudents = (batch) => {
    const enrollments = localStorageService.getBatchEnrollments(batch.id);
    const students = enrollments.map(enrollment => {
      const student = localStorageService.getItemById('students', enrollment.studentId);
      return student ? {
        ...student,
        enrollmentDate: enrollment.enrollmentDate,
        enrollmentId: enrollment.id
      } : null;
    }).filter(Boolean);

    setSelectedBatchStudents({
      batch,
      students,
      enrollmentCount: enrollments.length
    });
    setShowStudentsModal(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Groupes"
        description="Créez et gérez les groupes de cours avec horaires et formateurs"
        actions={
          <button type="button" onClick={() => setShowForm(!showForm)} className={formaPrimaryBtn}>
            <PlusIcon className="h-5 w-5" />
            <span>Nouveau groupe</span>
          </button>
        }
      />

      <UiCard padding={false} className="overflow-hidden">
        <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
          <div className="grid gap-6 p-6 sm:grid-cols-3 sm:gap-5">
            {groupsStats.map((stat, index) => (
              <motion.div
                key={stat.title}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.06 }}
                whileHover={{ y: -3 }}
                className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
              >
                <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                  />
                  <div className="relative flex gap-4">
                    <div
                      className={clsx(
                        'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                        stat.iconWrap
                      )}
                    >
                      <stat.icon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        {stat.title}
                      </p>
                      <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                        {stat.value}
                      </p>
                      <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                        {stat.hint}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="border-b border-slate-200 bg-white/40 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/35">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Cours
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              Filtrer la liste
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {courseFilterTabs.map((tab) => (
              <button
                key={tab.id || 'all'}
                type="button"
                onClick={() => setFilterCourse(tab.id)}
                className={clsx(
                  'inline-flex max-w-full items-center rounded-full px-3.5 py-2 text-sm font-medium transition-all',
                  filterCourse === tab.id
                    ? 'scale-[1.02] bg-forma-600 text-white shadow-md shadow-forma-600/25'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                )}
              >
                <span className="truncate">{tab.name}</span>
                <span
                  className={clsx(
                    'ml-2 min-w-[1.75rem] rounded-full px-2 py-0.5 text-center text-xs font-semibold tabular-nums',
                    filterCourse === tab.id
                      ? 'bg-white/20 text-white'
                      : 'bg-white/70 text-slate-800 dark:bg-slate-950 dark:text-slate-200'
                  )}
                >
                  {courseTabCounts[tab.id] ?? 0}
                </span>
              </button>
            ))}
          </div>
        </div>
      </UiCard>

        {/* Create/Edit Form */}
        {(showForm || showEditForm) && (
          <div className="mb-6">
            <BatchForm 
              onBatchCreated={handleBatchCreated}
              onCancel={() => {
                setShowForm(false);
                setShowEditForm(false);
                setEditingBatch(null);
              }}
              isEditing={showEditForm}
              batchData={editingBatch}
              classrooms={classrooms}
            />
          </div>
        )}

        {/* Groups List */}
        <div className="space-y-6">
          {isLoading ? (
            <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">Chargement des groupes...</p>
            </div>
          ) : filteredBatches.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-12 text-center">
              <TagIcon className="h-16 w-16 text-slate-900 dark:text-slate-50/30 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2">Aucun groupe trouvé</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {filterCourse ? 'Aucun groupe pour ce cours' : 'Créez votre premier groupe de cours'}
              </p>
              {!showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  className={`${formaPrimaryBtn} mx-auto flex items-center justify-center gap-2`}
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Créer le premier groupe</span>
                </button>
              )}
            </div>
          ) : (
            Object.entries(batchesByCourse).map(([courseId, courseBatches]) => {
              const course = Array.isArray(courses) ? courses.find(c => c.id === courseId) : null;
              return (
                <div key={courseId} className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="rounded-lg bg-forma-500/15 p-2 ring-1 ring-forma-500/25">
                        <AcademicCapIcon className="h-6 w-6 text-forma-600 dark:text-forma-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">{course?.title || 'Cours Inconnu'}</h3>
                        <p className="text-slate-600 dark:text-slate-400 text-sm">{courseBatches.length} groupe(s)</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {courseBatches.map(batch => (
                      <GroupCard
                        key={batch.id}
                        batch={batch}
                        onEdit={handleEditBatch}
                        onDelete={handleDeleteBatch}
                        onViewStudents={handleViewStudents}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Students Modal */}
        {showStudentsModal && selectedBatchStudents && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-900 rounded-3xl p-6 max-w-4xl w-full max-h-[80vh] overflow-hidden border border-slate-200 dark:border-slate-600">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
                    Étudiants inscrits - {selectedBatchStudents.batch.batchName}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">
                    {selectedBatchStudents.batch.courseName} • {selectedBatchStudents.batch.days.join(', ')} • {selectedBatchStudents.batch.startTime} - {selectedBatchStudents.batch.endTime}
                  </p>
                </div>
                <button
                  onClick={() => setShowStudentsModal(false)}
                  className="p-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-700 dark:text-slate-300">
                    Total: {selectedBatchStudents.students.length} étudiant(s)
                  </span>
                  <span className="text-slate-600 dark:text-slate-400 text-sm">
                    Capacité: {selectedBatchStudents.students.length}/{selectedBatchStudents.batch.maxCapacity}
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-50 dark:bg-slate-800/80 rounded-full mt-2 overflow-hidden">
                  <div 
                    className="h-full rounded-full bg-gradient-to-r from-forma-500 to-forma-700 transition-all duration-300" 
                    style={{ width: `${(selectedBatchStudents.students.length / selectedBatchStudents.batch.maxCapacity) * 100}%` }}
                  ></div>
                </div>
              </div>

              <div className="overflow-y-auto max-h-96">
                {selectedBatchStudents.students.length === 0 ? (
                  <div className="text-center py-12">
                    <UserGroupIcon className="h-16 w-16 text-slate-900 dark:text-slate-50/30 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2">Aucun étudiant inscrit</h3>
                    <p className="text-slate-600 dark:text-slate-400">Ce groupe n'a pas encore d'étudiants inscrits.</p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {selectedBatchStudents.students.map((student) => (
                      <div key={student.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-forma-500/15 ring-1 ring-forma-500/20">
                              <UserGroupIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
                            </div>
                            <div>
                              <h4 className="text-slate-900 dark:text-slate-50 font-medium">{student.name}</h4>
                              <p className="text-slate-600 dark:text-slate-400 text-sm">{student.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-slate-600 dark:text-slate-400 text-sm">
                              Inscrit le: {new Date(student.enrollmentDate).toLocaleDateString('fr-FR')}
                            </div>
                            <div className="text-slate-600 dark:text-slate-400 text-xs">
                              ID: {student.studentCode || student.id}
                            </div>
                          </div>
                        </div>
                        
                        {student.phone && (
                          <div className="mt-2 text-slate-600 dark:text-slate-400 text-sm">
                            📞 {student.phone}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6 pt-4 border-t border-slate-200 dark:border-slate-600">
                <button
                  onClick={() => setShowStudentsModal(false)}
                  className={`${formaPrimaryBtn} px-6 py-2`}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default GroupsPage;

